/**
 * @file sheets/bottom-sheet/use-bottom-sheet.ts
 *
 * Custom hook encapsulating drag-to-dismiss and snap point gesture logic
 * for Bottom Sheet components, using manual touch/pointer events to allow
 * seamless scroll-to-drag handoffs without locking native nested scrolling.
 */

import { animate, useMotionValue, useTransform } from "motion/react";
import {
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import type { BottomSheetSnapPoint } from "../shared/types";

const useIsomorphicLayoutEffect =
	typeof window !== "undefined" ? useLayoutEffect : useEffect;

// ─── Constants ────────────────────────────────────────────────────────────────

const DISMISS_THRESHOLD = 0.4;
const DISMISS_VELOCITY_THRESHOLD = 600;
const DEFAULT_SAFE_AREA_OFFSET = 56;
const BOUNDARY_DAMPENING_ZONE = 80;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const parseSnapPoint = (
	snap: BottomSheetSnapPoint,
	viewportHeight: number,
	naturalHeight: number,
	safeAreaOffset: number,
	parsedMaxHeight: number,
	hasUserSnapPoints: boolean,
): number => {
	const absoluteMaxHeight = viewportHeight - safeAreaOffset;
	const capHeight = hasUserSnapPoints
		? absoluteMaxHeight
		: Math.min(absoluteMaxHeight, parsedMaxHeight);

	if (typeof snap === "number") return Math.min(snap, capHeight);
	if (snap === "full") return capHeight;
	if (snap === "fit" || snap === "max-content")
		return Math.min(
			naturalHeight,
			Math.min(absoluteMaxHeight, parsedMaxHeight),
		);
	if (snap === "partiallyExpanded")
		return Math.min(Math.floor(viewportHeight * 0.5), capHeight);

	if (typeof snap === "string") {
		if (snap.endsWith("%")) {
			return Math.min((parseFloat(snap) / 100) * viewportHeight, capHeight);
		}
		if (snap.endsWith("px")) {
			return Math.min(parseFloat(snap), capHeight);
		}
		if (snap.endsWith("dvh") || snap.endsWith("vh")) {
			return Math.min((parseFloat(snap) / 100) * viewportHeight, capHeight);
		}
	}

	return Math.min(naturalHeight, capHeight);
};

const measureSafeAreaOffset = (): number => {
	if (typeof document === "undefined") return DEFAULT_SAFE_AREA_OFFSET;
	const el = document.createElement("div");
	el.style.cssText =
		"position:absolute;top:0;height:max(56px,env(safe-area-inset-top,0px));visibility:hidden;pointer-events:none";
	document.body.appendChild(el);
	const height = el.offsetHeight;
	document.body.removeChild(el);
	return height;
};

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseBottomSheetOptions {
	isOpen: boolean;
	onClose: () => void;
	snapPoints?: BottomSheetSnapPoint[];
	defaultSnapPoint?: BottomSheetSnapPoint;
	onSnapChange?: (snapPoint: BottomSheetSnapPoint) => void;
	gesturesEnabled?: boolean;
	isModal?: boolean;
	maxHeight?: string;
}

export interface UseBottomSheetReturn {
	scope: React.RefCallback<HTMLDivElement | null>;
	y: ReturnType<typeof useMotionValue<number>>;
	scrimOpacity: ReturnType<typeof useTransform<number, number>>;
	activeSnapPoint: BottomSheetSnapPoint;
	isContentScrollable: boolean;
	snapTo: (snapPoint: BottomSheetSnapPoint) => void;
	cycleSnapPoint: () => void;
	dragConstraints: { top: number; bottom: number };
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useBottomSheet({
	isOpen,
	onClose,
	snapPoints,
	defaultSnapPoint,
	onSnapChange,
	gesturesEnabled = true,
	isModal = false,
	maxHeight,
}: UseBottomSheetOptions): UseBottomSheetReturn {
	const [scopeNode, setScopeNode] = useState<HTMLDivElement | null>(null);
	const scopeNodeRef = useRef<HTMLDivElement | null>(null);

	const scope = useCallback((node: HTMLDivElement | null) => {
		scopeNodeRef.current = node;
		setScopeNode(node);
	}, []) as React.RefCallback<HTMLDivElement | null> & {
		current: HTMLDivElement | null;
	};

	Object.defineProperty(scope, "current", {
		get: () => scopeNodeRef.current,
		set: (v) => {
			scopeNodeRef.current = v;
		},
		configurable: true,
	});

	const y = useMotionValue(
		typeof window !== "undefined"
			? window.visualViewport
				? window.visualViewport.height
				: window.innerHeight
			: 800,
	);

	const [dimensions, setDimensions] = useState({
		viewportHeight:
			typeof window !== "undefined"
				? window.visualViewport
					? window.visualViewport.height
					: window.innerHeight
				: 800,
		naturalHeight: 0,
		safeAreaOffset: DEFAULT_SAFE_AREA_OFFSET,
	});

	const hasMeasuredAfterOpen = useRef(false);

	const measureDimensions = useCallback(() => {
		const node = scopeNodeRef.current;
		if (!node) return;

		const viewportHeight = window.visualViewport
			? window.visualViewport.height
			: window.innerHeight;
		const safeAreaOffset = measureSafeAreaOffset();

		const viewport = node.querySelector("[data-radix-scroll-area-viewport]");
		let naturalHeight = node.scrollHeight;

		if (viewport) {
			let nonScrollableHeight = 0;
			for (let i = 0; i < node.children.length; i++) {
				const child = node.children[i] as HTMLElement;
				if (!child.contains(viewport) && child !== viewport) {
					nonScrollableHeight += child.offsetHeight;
				}
			}
			naturalHeight = nonScrollableHeight + viewport.scrollHeight;
		}

		if (naturalHeight > 0) hasMeasuredAfterOpen.current = true;

		setDimensions((prev) => {
			if (
				prev.viewportHeight === viewportHeight &&
				prev.naturalHeight === naturalHeight &&
				prev.safeAreaOffset === safeAreaOffset
			) {
				return prev;
			}
			return { viewportHeight, naturalHeight, safeAreaOffset };
		});
	}, []);

	useIsomorphicLayoutEffect(() => {
		if (!isOpen || !scopeNode) return;
		measureDimensions();

		const observer = new ResizeObserver(measureDimensions);
		const viewport = scopeNode.querySelector(
			"[data-radix-scroll-area-viewport]",
		);

		if (viewport) {
			observer.observe(viewport);
			if (viewport.firstElementChild)
				observer.observe(viewport.firstElementChild);
		} else {
			observer.observe(scopeNode);
		}

		window.addEventListener("resize", measureDimensions);
		return () => {
			observer.disconnect();
			window.removeEventListener("resize", measureDimensions);
		};
	}, [isOpen, scopeNode, measureDimensions]);

	useIsomorphicLayoutEffect(() => {
		const vv = window.visualViewport;
		if (!isOpen || !vv) return;
		const handleResize = () => {
			document.documentElement.style.setProperty(
				"--sheet-vh",
				`${vv.height}px`,
			);
			measureDimensions();
		};
		vv.addEventListener("resize", handleResize);
		handleResize();
		return () => vv.removeEventListener("resize", handleResize);
	}, [isOpen, measureDimensions]);

	const { viewportHeight, naturalHeight, safeAreaOffset } = dimensions;

	const parsedMaxHeight = useMemo(() => {
		if (!maxHeight) return viewportHeight - safeAreaOffset;
		if (maxHeight.endsWith("px")) return parseFloat(maxHeight);
		if (maxHeight.endsWith("dvh") || maxHeight.endsWith("vh")) {
			return (parseFloat(maxHeight) / 100) * viewportHeight;
		}
		if (maxHeight.endsWith("%")) {
			return (parseFloat(maxHeight) / 100) * viewportHeight;
		}
		return parseFloat(maxHeight) || viewportHeight - safeAreaOffset;
	}, [maxHeight, viewportHeight, safeAreaOffset]);

	const snaps = useMemo((): BottomSheetSnapPoint[] => {
		if (snapPoints) return snapPoints;
		if (isModal) return ["partiallyExpanded", "full"];
		return ["fit"];
	}, [snapPoints, isModal]);

	const allowsViewportSnapExpansion =
		snapPoints !== undefined || snaps.length > 1;

	const snapHeights = useMemo(
		() =>
			snaps.map((snap) =>
				parseSnapPoint(
					snap,
					viewportHeight,
					naturalHeight,
					safeAreaOffset,
					parsedMaxHeight,
					allowsViewportSnapExpansion,
				),
			),
		[
			snaps,
			viewportHeight,
			naturalHeight,
			safeAreaOffset,
			parsedMaxHeight,
			allowsViewportSnapExpansion,
		],
	);

	const maxSheetHeight = useMemo(
		() => viewportHeight - safeAreaOffset,
		[viewportHeight, safeAreaOffset],
	);

	const snapYValues = useMemo(
		() => snapHeights.map((h) => Math.max(0, maxSheetHeight - h)),
		[snapHeights, maxSheetHeight],
	);

	const sortedSnaps = useMemo(() => {
		const combined = snaps.map((point, idx) => ({
			point,
			height: snapHeights[idx],
			y: snapYValues[idx],
		}));
		return combined.sort((a, b) => b.y - a.y);
	}, [snaps, snapHeights, snapYValues]);

	const [activeSnapIndex, setActiveSnapIndex] = useState(0);

	const activeSnapPoint = useMemo(
		() => sortedSnaps[activeSnapIndex]?.point ?? "fit",
		[sortedSnaps, activeSnapIndex],
	);

	const isContentScrollable = useMemo(
		() => sortedSnaps.length <= 1 || activeSnapIndex === sortedSnaps.length - 1,
		[sortedSnaps, activeSnapIndex],
	);

	const lowestSnapY = useMemo(() => sortedSnaps[0]?.y ?? 300, [sortedSnaps]);
	const scrimOpacity = useTransform(y, [lowestSnapY, maxSheetHeight], [1, 0]);

	const defaultSnap = useMemo(
		() => defaultSnapPoint ?? snaps[0] ?? "fit",
		[defaultSnapPoint, snaps],
	);

	const prevIsOpen = useRef(isOpen);

	useIsomorphicLayoutEffect(() => {
		if (isOpen && !prevIsOpen.current) {
			y.set(viewportHeight);
			hasMeasuredAfterOpen.current = false;
			const defaultIdx = sortedSnaps.findIndex((s) => s.point === defaultSnap);
			setActiveSnapIndex(defaultIdx !== -1 ? defaultIdx : 0);
		} else if (!isOpen && prevIsOpen.current) {
			setDimensions((prev) => ({ ...prev, naturalHeight: 0 }));
			void animate(y, viewportHeight, {
				type: "tween",
				duration: 0.22,
				ease: "easeIn",
			});
		}
		prevIsOpen.current = isOpen;
	}, [isOpen, y, viewportHeight, sortedSnaps, defaultSnap]);

	useEffect(() => {
		if (!isOpen || sortedSnaps.length === 0 || naturalHeight === 0) return;
		if (!hasMeasuredAfterOpen.current) return;

		const targetY = sortedSnaps[activeSnapIndex]?.y;
		if (targetY === undefined) return;

		void animate(y, targetY, { type: "spring", stiffness: 300, damping: 30 });
	}, [isOpen, sortedSnaps, activeSnapIndex, y, naturalHeight]);

	const snapTo = useCallback(
		(snapPoint: BottomSheetSnapPoint) => {
			const idx = sortedSnaps.findIndex((s) => s.point === snapPoint);
			if (idx === -1) return;
			setActiveSnapIndex(idx);
			void animate(y, sortedSnaps[idx].y, {
				type: "spring",
				stiffness: 300,
				damping: 30,
			});
			onSnapChange?.(snapPoint);
		},
		[sortedSnaps, y, onSnapChange],
	);

	const cycleSnapPoint = useCallback(() => {
		const nextIdx = activeSnapIndex + 1;
		if (nextIdx >= sortedSnaps.length) {
			onClose();
		} else {
			setActiveSnapIndex(nextIdx);
			void animate(y, sortedSnaps[nextIdx].y, {
				type: "spring",
				stiffness: 300,
				damping: 30,
			});
			onSnapChange?.(sortedSnaps[nextIdx].point);
		}
	}, [activeSnapIndex, sortedSnaps, y, onClose, onSnapChange]);

	const dragConstraints = useMemo(
		() => ({
			top: Math.max(0, sortedSnaps[sortedSnaps.length - 1]?.y ?? 0),
			bottom: viewportHeight,
		}),
		[sortedSnaps, viewportHeight],
	);

	// Seamless Manual Drag Gesture Handling (Touch + Mouse)
	useEffect(() => {
		if (!isOpen || !gesturesEnabled || !scopeNode) return;

		const node = scopeNode;
		const viewport = node.querySelector(
			"[data-radix-scroll-area-viewport]",
		) as HTMLElement | null;

		let startY = 0;
		let initialSheetY = 0;
		let isDragging = false;
		let lastY = 0;
		let lastTime = 0;
		let velocity = 0;
		let activeInput: "touch" | "mouse" | null = null;
		let dragStartTarget: EventTarget | null = null;
		let lastTouchTime = 0;
		let preventNextClick = false;

		const MOUSE_DRAG_THRESHOLD = 5;

		const updateVelocity = (currentY: number) => {
			const currentTime = performance.now();
			const deltaTime = currentTime - lastTime;
			if (deltaTime > 0) {
				velocity = ((currentY - lastY) / deltaTime) * 1000;
			}
			lastY = currentY;
			lastTime = currentTime;
		};

		const tryStartDrag = (deltaY: number): boolean => {
			if (viewport?.contains(dragStartTarget as Node)) {
				const isAtHighestSnap = activeSnapIndex === sortedSnaps.length - 1;

				if (!isAtHighestSnap) {
					isDragging = true;
					return true;
				}

				const scrollTop = viewport.scrollTop;
				const isPullingDown = deltaY > 0;
				if (!isDragging) {
					if (scrollTop <= 0 && isPullingDown) {
						isDragging = true;
						return true;
					}
					return false;
				}
				return true;
			}
			isDragging = true;
			return true;
		};

		const applyDragPosition = (deltaY: number) => {
			let nextY = initialSheetY + deltaY;
			if (nextY < dragConstraints.top) {
				const overdrag = dragConstraints.top - nextY;
				nextY = dragConstraints.top - overdrag * 0.15;
			}
			y.set(nextY);
		};

		const finalizeDrag = () => {
			if (!isDragging) return;
			preventNextClick = true;
			isDragging = false;

			const currentY = y.get();
			const lowestSnap = sortedSnaps[0];
			const lowestY = lowestSnap?.y ?? viewportHeight;
			const lowestHeight = lowestSnap?.height ?? 200;

			let finalVelocity = velocity;
			if (finalVelocity > 0) {
				const distanceToFloor = Math.max(0, currentY - lowestY);
				if (distanceToFloor < BOUNDARY_DAMPENING_ZONE) {
					const factor = distanceToFloor / BOUNDARY_DAMPENING_ZONE;
					finalVelocity *= factor * factor;
					if (velocity >= DISMISS_VELOCITY_THRESHOLD) {
						finalVelocity = Math.max(
							finalVelocity,
							DISMISS_VELOCITY_THRESHOLD * 0.1,
						);
					}
				}
			}

			const dragOffset = Math.max(0, currentY - lowestY);
			const shouldDismiss =
				finalVelocity > DISMISS_VELOCITY_THRESHOLD ||
				dragOffset > lowestHeight * DISMISS_THRESHOLD;

			if (shouldDismiss) {
				onClose();
				return;
			}

			// Snap to closest
			let closestIdx = 0;
			let minDiff = Infinity;
			for (let i = 0; i < sortedSnaps.length; i++) {
				const diff = Math.abs(sortedSnaps[i].y - currentY);
				if (diff < minDiff) {
					minDiff = diff;
					closestIdx = i;
				}
			}

			setActiveSnapIndex(closestIdx);
			void animate(y, sortedSnaps[closestIdx].y, {
				type: "spring",
				stiffness: 380,
				damping: 31.19,
			});
			onSnapChange?.(sortedSnaps[closestIdx].point);
		};

		// ── Touch handlers ──────────────────────────────────────────────────
		const onTouchStart = (e: TouchEvent) => {
			lastTouchTime = Date.now();
			activeInput = "touch";
			dragStartTarget = e.target;
			startY = e.touches[0].clientY;
			initialSheetY = y.get();
			isDragging = false;
			lastY = startY;
			lastTime = performance.now();
			velocity = 0;
		};

		const onTouchMove = (e: TouchEvent) => {
			if (!e.touches[0] || activeInput !== "touch") return;
			const currentY = e.touches[0].clientY;
			const deltaY = currentY - startY;
			updateVelocity(currentY);

			if (tryStartDrag(deltaY)) {
				e.preventDefault();
				applyDragPosition(deltaY);
			}
		};

		const onTouchEnd = () => {
			if (activeInput !== "touch") return;
			activeInput = null;
			finalizeDrag();
			dragStartTarget = null;
		};

		// ── Mouse handlers (desktop drag support) ───────────────────────────
		const onMouseDown = (e: MouseEvent) => {
			if (e.button !== 0) return;
			// Ignore synthesized mouse events after touch interaction
			if (Date.now() - lastTouchTime < 500) return;
			activeInput = "mouse";
			dragStartTarget = e.target;
			startY = e.clientY;
			initialSheetY = y.get();
			isDragging = false;
			lastY = startY;
			lastTime = performance.now();
			velocity = 0;
		};

		const onMouseMove = (e: MouseEvent) => {
			if (activeInput !== "mouse") return;
			const currentY = e.clientY;
			const deltaY = currentY - startY;

			// Require minimum movement to distinguish click from drag
			if (!isDragging && Math.abs(deltaY) < MOUSE_DRAG_THRESHOLD) return;

			updateVelocity(currentY);

			if (tryStartDrag(deltaY)) {
				e.preventDefault();
				document.body.style.userSelect = "none";
				applyDragPosition(deltaY);
			}
		};

		const onMouseUp = () => {
			if (activeInput !== "mouse") return;
			activeInput = null;
			document.body.style.userSelect = "";
			finalizeDrag();
			dragStartTarget = null;
		};

		// ── Click prevention after drag ─────────────────────────────────────
		const onClickCapture = (e: MouseEvent) => {
			if (preventNextClick) {
				e.stopPropagation();
				preventNextClick = false;
			}
		};

		// Register events
		node.addEventListener("touchstart", onTouchStart, { passive: true });
		node.addEventListener("touchmove", onTouchMove, { passive: false });
		node.addEventListener("touchend", onTouchEnd);
		node.addEventListener("touchcancel", onTouchEnd);
		node.addEventListener("mousedown", onMouseDown);
		document.addEventListener("mousemove", onMouseMove);
		document.addEventListener("mouseup", onMouseUp);
		node.addEventListener("click", onClickCapture, true);

		return () => {
			node.removeEventListener("touchstart", onTouchStart);
			node.removeEventListener("touchmove", onTouchMove);
			node.removeEventListener("touchend", onTouchEnd);
			node.removeEventListener("touchcancel", onTouchEnd);
			node.removeEventListener("mousedown", onMouseDown);
			document.removeEventListener("mousemove", onMouseMove);
			document.removeEventListener("mouseup", onMouseUp);
			node.removeEventListener("click", onClickCapture, true);
			document.body.style.userSelect = "";
		};
	}, [
		isOpen,
		gesturesEnabled,
		sortedSnaps,
		activeSnapIndex,
		y,
		dragConstraints.top,
		viewportHeight,
		onClose,
		onSnapChange,
		scopeNode,
	]);

	return {
		scope,
		y,
		scrimOpacity,
		activeSnapPoint,
		isContentScrollable,
		snapTo,
		cycleSnapPoint,
		dragConstraints,
	};
}
